<svelte:options
  customElement={{
    tag: "oc-checkbox-v1",

    extend: window.__components.extend({
      formAssociated: true,
      inputBehavior: true,
    }),
    props: {
      fitContent: { type: "Boolean", attribute: "fit-content" },
      name: { type: "String", reflect: true }, /*                                    */
      labelPlacement: { type: "String", attribute: "label-placement" },
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      variant: { type: "String" },
      required: { type: "Boolean" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      hint: { type: "String" },
      validationMessage: { type: "String", attribute: "validation-message" },
    },
  }}
/>

<script lang="ts">
  import { exposeStatesForCSS } from "@otto-ec/otto-components-utils/utils";
  import {
    ariaDescriptionWithValidation,
    implicitSubmit,
    refireNonComposableNativeEvent,
    stopLabelClickPropagation,
  } from "../../../common/actions";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";
  import HintV1 from "../../../common/components/form/HintV1.svelte";
  import ValidationMessageV1 from "../../../common/components/form/ValidationMessageV1.svelte";
  import { useSlots } from "../../../common/utils/useSlots.svelte";

  import type { Props } from "./CheckboxV1.types";

  let {
    name = undefined,
    labelPlacement = undefined,
    value = "on",
    checked = false,
    variant = "default",
    disabled = false,
    ocAriaLabel = undefined,
    hint = undefined,
    required = false,
    fitContent = false,
    validationMessage = undefined,
    hideDetails = false,
    internals,
  }: Props & {
    internals: ElementInternals;
    hideDetails?: boolean;
  } = $props();

  const Host = $host();

  const slots = useSlots(Host);

  export function resetForm(): void {
    /*                            */
    checked = Host.hasAttribute("checked");
  }

  const computedVariant = $derived(validationMessage ? "error" : variant);

  $effect(() => {
    internals.setFormValue(checked ? value : null);
  });

  $effect(() => {
    exposeStatesForCSS(Host, { checked });
  });

  const oninput = ({ target }: Event): void => {
    checked = (target as HTMLInputElement).checked;
  };
</script>

<label
  class="checkbox"
  class:checkbox--error={computedVariant === "error"}
  class:checkbox--fit-content={fitContent}
  use:stopLabelClickPropagation
>
  <!-- Left Label Placement -->
  {#if slots.default && labelPlacement === "left"}
    <span class="checkbox__label" id="label" aria-hidden="true">
      <slot />
      <FormLabelIconV1 variant={computedVariant} />
    </span>
  {/if}

  <span>
    <input
      type="checkbox"
      class="checkbox__input"
      {checked}
      {oninput}
      {name}
      {disabled}
      use:refireNonComposableNativeEvent={Host}
      use:implicitSubmit={internals}
      use:ariaDescriptionWithValidation={{ validationMessage, hint }}
      aria-labelledby={ocAriaLabel ? undefined : "label"}
      aria-label={ocAriaLabel}
      aria-required={required}
    />
    <!-- The box displays the outline and the icon. This is needed because input element doesn't support pseudo elements. -->
    <span class="checkbox__box"></span>
  </span>

  <!-- Right Label Placement -->
  {#if slots.default && labelPlacement !== "left"}
    <span class="checkbox__label" id="label" aria-hidden="true">
      <slot />
      <FormLabelIconV1 variant={computedVariant} />
    </span>
  {/if}
</label>

{#if !hideDetails}
  <ValidationMessageV1 {validationMessage} />
  <HintV1 {hint} {disabled} />
{/if}

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    :host {
      display: block;
    }

    .checkbox {
      display: flex;
      /*                                             */
      /*                                      */
      gap: tokens.$oc-semantic-spacing-75;
      color: tokens.$oc-component-form-default-label-color;
      font: tokens.$oc-component-form-field-input-font;

      &--fit-content {
        width: fit-content;
      }
    }

    .checkbox__input {
      all: unset;
      position: absolute;
      /*                                                        */
      /*                                      */
      width: tokens.$oc-base-dimension-relative-24;
      /*                                                         */
      /*                                      */
      height: tokens.$oc-base-dimension-relative-24;

      /*                 */
      /*                                                                                  */
      /*                                      */
      --oc-floating-focus-v1-radius: #{tokens.$oc-semantic-border-radius-50};
    }

    .checkbox__label {
      flex: 1;
      min-width: 0;
    }

    .checkbox__box {
      position: relative;
      display: block;
      /*                                                        */
      /*                                      */
      width: tokens.$oc-base-dimension-relative-24;
      /*                                                         */
      /*                                      */
      height: tokens.$oc-base-dimension-relative-24;

      outline: 1px solid tokens.$oc-component-form-default-border-color;
      /*                                                               */
      /*                                      */
      border-radius: tokens.$oc-semantic-border-radius-50;
      background-color: tokens.$oc-component-form-background-color;

      &:before {
        $center-icon: calc(50% - tokens.$oc-component-form-checkbox-100-icon-size / 2);
        content: "";
        display: block;
        position: absolute;
        width: tokens.$oc-component-form-checkbox-100-icon-size;
        height: tokens.$oc-component-form-checkbox-100-icon-size;
        left: $center-icon;
        top: $center-icon;
        bottom: $center-icon;
        right: $center-icon;
        mask-image: url("/assets-static/icons/pl_icon_check50.svg");
        background-color: tokens.$oc-component-form-default-icon-color-checked;
        visibility: hidden;
      }
    }

    /*                   */
    .checkbox__input:checked ~ .checkbox__box {
      background-color: tokens.$oc-component-form-background-color-checked;

      &:before {
        visibility: visible;
      }
    }

    /*                 */
    .checkbox--error {
      color: tokens.$oc-component-form-error-label-color;

      .checkbox__box {
        outline-color: tokens.$oc-component-form-error-border-color;
      }

      .checkbox__input:checked ~ .checkbox__box {
        background-color: tokens.$oc-component-form-error-background-color-checked;
        box-shadow: 0 0 0 1px tokens.$oc-component-form-error-background-color-checked;
      }
    }

    /*                 */
    @media (hover: hover) {
      :host(:enabled) {
        .checkbox:hover {
          cursor: pointer;

          .checkbox__box {
            outline-width: tokens.$oc-semantic-focus-outline-width;
          }
        }
      }
    }

    /*                  */
    :host(:enabled) {
      .checkbox__input:active ~ .checkbox__box {
        outline-width: tokens.$oc-semantic-focus-outline-width;
      }

      .checkbox:not(.checkbox--error) .checkbox__input:active ~ .checkbox__box {
        outline-color: tokens.$oc-component-form-default-border-color-active;
      }
    }

    /*                    */
    :host(:disabled) {
      .checkbox {
        color: tokens.$oc-component-form-disabled-label-color;
      }

      .checkbox__box {
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        &:before {
          background: tokens.$oc-component-form-disabled-icon-color;
        }
      }
    }
  }
</style>
